import React, { useEffect, useMemo } from 'react';
import { Stack } from "react-bootstrap";
import { useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import { useQuery } from "@tanstack/react-query";
import { getTeams, getAthletes } from "data/Querys";
import Loader from "components/general/Loader";
import NotAvailable from "components/common/NotAvailable";
import SelectorDiscipline from "components/common/selectors/selector-discipline/selectorDiscipline";
import SelectorNoc from 'components/common/selectors/selector-noc';
import EntriesDisciplineTable from 'components/common/entries/EntriesDisciplineTable';
import { ALL_URL, PROCESSING_TYPE, SELECTOR_DISPLAY_TYPE, processFilteredData } from 'helpers/utils';
import { useGetH1 } from "helpers/commonCodes";

export default function EntriesByDiscipline() {
    const [dataState, setDataState] = React.useState([]);
    const { mirs_competition, mirs_language, mirs_discipline, mirs_noc, mirs_event } = useUrlFriendlyToParameters()
    const dataReports = useGetH1({ mirs_competition: mirs_competition });
    // Memoize the arrHasTeams array
    const arrHasTeams = useMemo(() => {
        let teams = [];
        dataReports?.h1s
            .filter(r => r.orisNo === 'C31T')
            ?.forEach((r) => {
                teams.push(r.disciplineCode);
            });
        return teams;
    }, [dataReports]);

    const isTeam = arrHasTeams.includes(mirs_discipline);

    const { data: athletesData, isError: isAthletesError, isLoading: isAthletesLoading } = useQuery({
        queryKey: ['athletes', mirs_competition, mirs_language, 'A'],
        queryFn: getAthletes,
        refetchInterval: 0,
    });

    const { data: teamsData, isError: isTeamsError, isLoading: isTeamsLoading } = useQuery({
        queryKey: ['teams', mirs_competition, mirs_language, 'A'],
        queryFn: getTeams,
        refetchInterval: 0,
    });

    const isLoading = isTeamsLoading || isAthletesLoading;
    const isError = isTeamsError || isAthletesError;

    useEffect(() => {
        let filteredResultsAthlete = athletesData?.persons || [];
        let filteredResultsTeam = teamsData?.teams || [];

        if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
            filteredResultsAthlete = filteredResultsAthlete?.filter(item => 
                item?.disciplines?.some(disc => disc?.code === mirs_discipline)
            );
            filteredResultsTeam = filteredResultsTeam?.filter(item => 
                item?.discipline?.code === mirs_discipline
            );
        }
        if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
            filteredResultsAthlete = filteredResultsAthlete?.filter(item => 
                item?.organisation?.code === mirs_noc
            );
            filteredResultsTeam = filteredResultsTeam?.filter(item => 
                item?.organisation?.code === mirs_noc
            );
        }

        filteredResultsAthlete = processFilteredData(filteredResultsAthlete, mirs_discipline, PROCESSING_TYPE.PERSON);
        filteredResultsTeam = processFilteredData(filteredResultsTeam, mirs_discipline, PROCESSING_TYPE.TEAM);


        // Create a new filteredResults array
        let combinedResults = [];

        // Add team disciplines from filteredResultsTeam
        arrHasTeams?.forEach(disciplineCode => {
            const teamResults = filteredResultsTeam?.filter(item => item?.discipline?.code === disciplineCode);
            combinedResults = [...combinedResults, ...teamResults];
        });

        // Add non-team disciplines from filteredResultsAthlete
        filteredResultsAthlete?.forEach(athleteResult => {
            if (!arrHasTeams?.includes(athleteResult?.discipline?.code)) {
                combinedResults.push(athleteResult);
            }
        });

        setDataState(combinedResults);
    }, [athletesData, teamsData, mirs_event, mirs_noc, mirs_discipline, arrHasTeams, isTeam]);

    if (isError) {
        return <NotAvailable label="LabelNoDataAvailable" />;
    }
    if (isLoading) {
        return <Loader />;
    }
    return (
        <div className='mirs-page-container'>
            <Stack direction="horizontal" className='selectors-container' >
                <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="entriesbydiscipline" />
                <SelectorNoc display_type={SELECTOR_DISPLAY_TYPE.COMBO} page='entriesbydiscipline' />
            </Stack>
            <EntriesDisciplineTable data={dataState} footerData={calculateFooterData(dataState)} fromPage={'entriesbydiscipline'} />
        </div>
    )
};


export function calculateFooterData(dataState) {
    const footerData = [
        {
            id: 'Discipline',
            calculate: (dataState) => 'Total',
        },
        {
            id: 'M',
            calculate: (dataState) => dataState.reduce((total, row) => total + (row?.gender?.M || 0), 0),
        },
        {
            id: 'F',
            calculate: (dataState) => dataState.reduce((total, row) => total + (row?.gender?.F || 0), 0),
        },
        {
            id: 'T',
            calculate: (dataState) => dataState.reduce((total, row) => total + (row?.gender?.T || 0), 0),
        },
    ];
    return footerData;
}

